import React, { useEffect, useRef, useState, memo } from 'react';
import { loadModules } from 'esri-loader';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { requestThrottler } from '../../../../services/requestThrottler';

const LiveMap = memo(function LiveMap({
  webmapID,
  token,
  addressInfo,
  selectedBlock = null,
  defaultSettings = true,
  showDebugInfo = false,
}) {
  console.debug({
    webmapID,
    token,
    addressInfo,
    selectedBlock,
    defaultSettings,
  });

  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isClassBreaksLoading, setIsClassBreaksLoading] = useState(false);
  const [throttlerStatus, setThrottlerStatus] = useState(null);
  const viewRef = useRef(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [lastErrorInfo, setLastErrorInfo] = useState(null);

  const recordError = (context, err) => {
    const info = {
      context,
      name: err?.name || null,
      code: err?.code || err?.error?.code || null,
      message: err?.message || String(err),
    };
    setLastErrorInfo(info);
    setErrorMessage(info.message);
  };

  const handleManualReload = () => {
    console.log('[MAP_RELOAD] Manual reload button clicked');
    if (viewRef.current) {
      try {
        viewRef.current.destroy();
      } catch (e) {
        console.warn('Error destroying existing view during manual reload:', e);
      }
      viewRef.current = null;
    }
    setIsMapLoaded(false);
    setErrorMessage(null);
    setLastErrorInfo(null);
    setReloadNonce(prev => prev + 1);
  };

  // Update throttler status for debug info
  useEffect(() => {
    if (!showDebugInfo) return;

    const updateStatus = () => {
      setThrottlerStatus(requestThrottler.getStatus());
    };

    updateStatus();
    const interval = setInterval(updateStatus, 1000);

    return () => clearInterval(interval);
  }, [showDebugInfo]);

  useEffect(() => {
    // Skip if no required data or map already exists
    if (!webmapID || !token || !mapRef.current) return;

    // Cleanup existing view
    if (viewRef.current) {
      viewRef.current.destroy();
      viewRef.current = null;
      setIsMapLoaded(false);
    }

    // reset previous errors on new load attempt
    setErrorMessage(null);
    setLastErrorInfo(null);

    const mapKey = `${webmapID}-${defaultSettings}-${
      selectedBlock?.serviceURL || 'default'
    }`;

    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/layers/FeatureLayer',
      'esri/widgets/Legend',
      'esri/config',
      'esri/smartMapping/statistics/classBreaks',
      'esri/WebMap',
      'esri/geometry/Extent',
      'esri/Graphic',
      'esri/layers/GraphicsLayer',
    ])
      .then(
        ([
          Map,
          MapView,
          FeatureLayer,
          Legend,
          esriConfig,
          classBreaks,
          WebMap,
          Extent,
          Graphic,
          GraphicsLayer,
        ]) => {
          esriConfig.apiKey = token;

          const extent = addressInfo
            ? new Extent({
                xmin:
                  addressInfo.address_xmin === addressInfo.address_xmax
                    ? addressInfo.address_longitude - 0.01
                    : addressInfo.address_xmin,
                ymin:
                  addressInfo.address_ymin === addressInfo.address_ymax
                    ? addressInfo.address_latitude - 0.01
                    : addressInfo.address_ymin,
                xmax:
                  addressInfo.address_xmin === addressInfo.address_xmax
                    ? addressInfo.address_longitude + 0.01
                    : addressInfo.address_xmax,
                ymax:
                  addressInfo.address_ymin === addressInfo.address_ymax
                    ? addressInfo.address_latitude + 0.01
                    : addressInfo.address_ymax,
                spatialReference: {
                  wkid: addressInfo.spatialReference?.wkid || 4326,
                },
              })
            : null;

          if (defaultSettings) {
            // Initialize WebMap with proper error handling
            const webmap = new WebMap({
              portalItem: {
                id: webmapID,
              },
            });

            // Handle layer loading errors on the webmap
            // WebMap objects do have a when() method
            if (typeof webmap.when === 'function') {
              webmap
                .when(() => {
                  // Check if layers collection exists and has items
                  if (webmap.layers && webmap.layers.items) {
                    webmap.layers.items.forEach(layer => {
                      if (layer && typeof layer.when === 'function') {
                        layer.when(
                          () => {
                            console.log(
                              `Layer ${layer.title} loaded successfully`,
                            );
                          },
                          error => {
                            console.warn(
                              `Failed to load layer ${layer.title}:`,
                              error,
                            );
                            // Optionally remove the failed layer to prevent it from blocking the map
                            webmap.remove(layer);
                          },
                        );
                      }
                    });
                  }
                })
                .catch(error => {
                  console.warn('Error in webmap initialization:', error);
                  recordError('webmap-initialization', error);
                });
            }

            // Create map view with error handling
            const view = new MapView({
              container: mapRef.current,
              map: webmap,
              extent: extent,
              zoom: 14,
              navigation: {
                mouseWheelZoomEnabled: false,
              },
            });

            viewRef.current = view;

            // Handle successful view loading
            view
              .when(() => {
                console.log('[MAP_LOAD] Map loaded successfully');
                setIsMapLoaded(true);
                setErrorMessage(null);
                setLastErrorInfo(null);
              })
              .catch(err => {
                console.error('[MAP_LOAD] Map view failed to load:', err);
                setIsMapLoaded(false);
                recordError('map-view-load', err);
              });

            // Add UI components to the view
            view.ui.add(createResponsiveLegend(Legend, view), 'top-right');

            if (
              addressInfo?.address_longitude &&
              addressInfo?.address_latitude
            ) {
              const customSymbol = {
                type: 'picture-marker',
                url: marker,
                width: '50px',
                height: '100px',
              };

              view.graphics.add(
                new Graphic({
                  geometry: {
                    type: 'point',
                    longitude: addressInfo.address_longitude,
                    latitude: addressInfo.address_latitude,
                  },
                  symbol: customSymbol,
                }),
              );
            }
          } else if (selectedBlock) {
            // Validate required selectedBlock parameters before proceeding
            const fieldName = selectedBlock?.outfields?.[0];
            if (!selectedBlock?.serviceURL) {
              const msg = 'Selected block is missing serviceURL.';
              console.error(msg, selectedBlock);
              setErrorMessage(msg);
              setLastErrorInfo({
                context: 'input-validation',
                name: 'MissingServiceURL',
                code: 'E_INPUT',
                message: msg,
              });
              setIsMapLoaded(false);
              return;
            }
            if (!fieldName) {
              const msg =
                "Selected block is missing a valid 'outfields[0]' to classify.";
              console.error(msg, selectedBlock);
              setErrorMessage(msg);
              setLastErrorInfo({
                context: 'input-validation',
                name: 'MissingOutfield',
                code: 'E_INPUT',
                message: msg,
              });
              setIsMapLoaded(false);
              return;
            }
            const defaultSym = {
              type: 'simple-fill',
              outline: {
                color: selectedBlock.boundaries
                  ? [57, 88, 92, 1]
                  : [255, 255, 255, 0],
                width: '1px',
              },
            };

            const renderer = {
              type: 'simple',
              symbol: defaultSym,
              label: `U.S. ${selectedBlock.group}`,
              visualVariables: [
                {
                  type: 'color',
                  field: fieldName,
                  stops: [],
                },
              ],
            };

            const customLayer = new FeatureLayer({
              url: selectedBlock.serviceURL,
              renderer: renderer,
              title: selectedBlock.title + ` (${selectedBlock.group})`,
              popupTemplate: {
                title: selectedBlock.title + ` (${selectedBlock.group})`,
                content: `{${fieldName}}`,
                fieldInfos: [
                  {
                    fieldName: fieldName,
                    format: {
                      digitSeparator: true,
                      places: 0,
                    },
                  },
                ],
              },
            });

            customLayer.opacity = 0.6;
            customLayer.minScale = 0;
            customLayer.maxScale = 0;

            // Set loading state for class breaks
            setIsClassBreaksLoading(true);
            console.log(
              `[LiveMap] Starting class breaks request for field: ${fieldName}`,
            );

            requestThrottler
              .execute(() => {
                return classBreaks({
                  layer: customLayer,
                  field: fieldName,
                  classificationMethod: 'quantile',
                });
              })
              .then(function (response) {
                console.log(
                  `[LiveMap] Class breaks request successful for field: ${fieldName}`,
                );
                setIsClassBreaksLoading(false);
                const breakInfos = response.classBreakInfos;

                const dynamicStops = breakInfos.map((info, index) => {
                  const colors = [
                    '#e66565',
                    '#f1a178',
                    '#f9d58d',
                    '#d9f0ae',
                    '#a8e9c7',
                  ];
                  return {
                    value: info.maxValue,
                    color: colors[index] || '#e66565',
                    label: info.label,
                  };
                });

                customLayer.renderer.visualVariables[0].stops = dynamicStops;

                // Create map with proper error handling
                const map = new Map({
                  basemap: 'gray-vector',
                  layers: [customLayer],
                });

                // Handle layer loading errors directly
                // Regular Map objects don't have when() method, only WebMap does
                // So we handle each layer individually
                if (map.layers && map.layers.items) {
                  map.layers.items.forEach(layer => {
                    // Only call when() if the layer supports it
                    if (layer && typeof layer.when === 'function') {
                      layer.when(
                        () => {
                          console.log(
                            `Layer ${layer.title} loaded successfully`,
                          );
                        },
                        error => {
                          console.warn(
                            `Failed to load layer ${layer.title}:`,
                            error,
                          );
                          // Remove failed layers to prevent them from blocking map display
                          map.remove(layer);
                        },
                      );
                    }
                  });
                }

                // Create the view with error handling
                const view = new MapView({
                  container: mapRef.current,
                  map: map,
                  extent: extent,
                  zoom: 14,
                  navigation: {
                    mouseWheelZoomEnabled: false,
                  },
                });

                viewRef.current = view;

                // Handle map loading
                view
                  .when(() => {
                    console.log(
                      '[MAP_LOAD] Feature layer map loaded successfully',
                    );
                    setIsMapLoaded(true);
                    setErrorMessage(null);
                    setLastErrorInfo(null);
                  })
                  .catch(err => {
                    console.error(
                      '[MAP_LOAD] Feature layer map failed to load:',
                      err,
                    );
                    setIsMapLoaded(false);
                    recordError('feature-layer-view-load', err);
                  });

                // Add UI components to the view
                view.ui.add(
                  new Legend({
                    view: view,
                  }),
                  'top-right',
                );

                if (
                  addressInfo?.address_longitude &&
                  addressInfo?.address_latitude
                ) {
                  const customSymbol = {
                    type: 'picture-marker',
                    url: marker,
                    width: '50px',
                    height: '100px',
                  };

                  view.graphics.add(
                    new Graphic({
                      geometry: {
                        type: 'point',
                        longitude: addressInfo.address_longitude,
                        latitude: addressInfo.address_latitude,
                      },
                      symbol: customSymbol,
                    }),
                  );
                }
              })
              .catch(err => {
                console.error(
                  `[LiveMap] Class breaks failed for field ${fieldName}:`,
                  err,
                );
                setIsClassBreaksLoading(false);
                setIsMapLoaded(false);

                // Enhanced error handling based on error type
                if (err?.message?.includes('timeout')) {
                  const customErr = {
                    name: 'ClassBreaksTimeout',
                    code: 'E_TIMEOUT',
                    message: `Class breaks request timed out for field '${fieldName}'. The request was retried but failed. This may be due to server overload.`,
                  };
                  recordError('class-breaks-timeout', customErr);
                } else if (err?.name === 'class-breaks:missing-parameters') {
                  const customErr = {
                    name: err?.name,
                    code: 'E_CLASS_BREAKS_PARAMS',
                    message:
                      "Unable to compute class breaks: missing 'layer' or 'field'. Ensure 'outfields[0]' exists and is a numeric field.",
                  };
                  recordError('class-breaks', customErr);
                } else {
                  const customErr = {
                    name: err?.name || 'ClassBreaksError',
                    code: err?.code || 'E_UNKNOWN',
                    message: `Class breaks request failed: ${
                      err?.message || 'Unknown error'
                    }`,
                  };
                  recordError('class-breaks', customErr);
                }
              });
          }
        },
      )
      .catch(err => {
        console.error('Failed to load ESRI modules:', err);
        setIsMapLoaded(false);
        recordError('esri-modules-load', err);
      });

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
        setIsMapLoaded(false);
      }
      // Clear any pending class breaks requests for this component
      setIsClassBreaksLoading(false);
    };
  }, [
    webmapID,
    token,
    defaultSettings,
    selectedBlock?.serviceURL,
    addressInfo?.address_longitude,
    addressInfo?.address_latitude,
    reloadNonce,
  ]);

  return (
    <Box
      sx={{
        width: {
          xs: '18rem',
          sm: '100%',
        },
        height: {
          xs: '15rem',
          sm: '30rem',
        },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {(!isMapLoaded || isClassBreaksLoading) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {errorMessage ? (
            <Box
              sx={{
                p: 2,
                maxWidth: '90%',
                bgcolor: 'rgba(255,255,255,0.95)',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant='subtitle1'
                sx={{ color: '#b00020', mb: 0.5 }}
              >
                Error loading map
              </Typography>
              <Typography variant='body2' sx={{ color: '#333', mb: 2 }}>
                {errorMessage}
              </Typography>
              <Box sx={{ mt: 1, width: '100%' }}>
                <Box sx={{ mt: 0.5 }}>
                  {webmapID && (
                    <Typography
                      variant='caption'
                      sx={{ color: '#555', display: 'block' }}
                    >
                      {`WebMap ID: ${webmapID}`}
                    </Typography>
                  )}
                  {selectedBlock?.serviceURL && (
                    <Typography
                      variant='caption'
                      sx={{ color: '#555', display: 'block' }}
                    >
                      {`Service URL: ${selectedBlock.serviceURL}`}
                    </Typography>
                  )}
                  {lastErrorInfo?.name && (
                    <Typography
                      variant='caption'
                      sx={{ color: '#555', display: 'block' }}
                    >
                      {`Error: ${lastErrorInfo.name}${
                        lastErrorInfo.code
                          ? ` (code ${lastErrorInfo.code})`
                          : ''
                      }`}
                    </Typography>
                  )}
                  {lastErrorInfo?.context && (
                    <Typography
                      variant='caption'
                      sx={{ color: '#777', display: 'block' }}
                    >
                      {`Context: ${lastErrorInfo.context}`}
                    </Typography>
                  )}
                  <Typography
                    variant='caption'
                    sx={{ color: '#777', display: 'block', mt: 0.5 }}
                  >
                    Check network connectivity and token validity, or try
                    reload.
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress
                sx={{
                  color: '#4FA490',
                }}
              />
              <Typography
                variant='caption'
                sx={{
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                {isClassBreaksLoading
                  ? 'Processing map data...'
                  : 'Loading map...'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          zIndex: 10,
        }}
      >
        <Button
          size='small'
          variant='contained'
          onClick={handleManualReload}
          sx={{
            backgroundColor: '#4FA490',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#39585C' },
            boxShadow: 'none',
          }}
        >
          Reload map
        </Button>
      </Box>

      {/* Debug Info Panel */}
      {showDebugInfo && throttlerStatus && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: 1,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            maxWidth: '200px',
          }}
        >
          <Typography
            variant='caption'
            sx={{ fontWeight: 'bold', display: 'block' }}
          >
            Throttler Status:
          </Typography>
          <Typography variant='caption' sx={{ display: 'block' }}>
            Running: {throttlerStatus.running}/{throttlerStatus.maxConcurrent}
          </Typography>
          <Typography variant='caption' sx={{ display: 'block' }}>
            Queued: {throttlerStatus.queued}
          </Typography>
          <Typography variant='caption' sx={{ display: 'block' }}>
            Success: {throttlerStatus.stats.successfulRequests}
          </Typography>
          <Typography variant='caption' sx={{ display: 'block' }}>
            Failed: {throttlerStatus.stats.failedRequests}
          </Typography>
          <Typography variant='caption' sx={{ display: 'block' }}>
            Retried: {throttlerStatus.stats.retriedRequests}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

const createResponsiveLegend = (Legend, view) => {
  // Add CSS to handle responsive legend
  const style = document.createElement('style');
  style.textContent = `
    .esri-legend {
      max-height: 150px !important; /* Smaller height on mobile */
      max-width: 150px !important;  /* Smaller width on mobile */
      overflow-y: auto !important;  /* Enable scrolling if content is too long */
      overflow-x: auto !important;
      background-color: rgba(255, 255, 255, 0.9) !important; /* Semi-transparent background */
    }

    @media screen and (min-width: 600px) {
      .esri-legend {
        max-height: 300px !important; /* Larger height on desktop */
        max-width: 250px !important;  /* Larger width on desktop */
      }
    }

    .esri-legend__service {
      padding: 8px !important;
    }
  `;
  document.head.appendChild(style);

  return new Legend({
    view: view,
  });
};

export default LiveMap;
